import { useEffect, useRef, useState, type ReactNode } from 'react'
import { theme } from '../theme'
import { Ic } from '../icons'
import type { Me, AccountRef } from '../types'
import { navigate } from '../router'
import { switchTo, roleLabel } from './AccountSharing'

export function Wordmark({ size = 26 }: { size?: number }) {
  return (
    <img
      src="/mila-name-transparent.png"
      alt="Mila"
      style={{ height: size, width: 'auto', maxWidth: 'none', display: 'block' }}
    />
  )
}

const navItems = [
  { id: 'plan',    label: 'Plan',    icon: Ic.plan },
  { id: 'recipes', label: 'Recipes', icon: Ic.book },
  { id: 'pantry',  label: 'Pantry',  icon: Ic.jar },
  { id: 'preferences', label: 'Preferences', icon: Ic.user },
] as const

export type TabId = (typeof navItems)[number]['id']

export function Sidebar({ tab, setTab, me, plannedThisWeek, onLogout }: {
  tab: TabId
  setTab: (t: TabId) => void
  me: Me
  plannedThisWeek: number
  onLogout: () => void
}) {
  return (
    <aside style={{
      width: 220, flex: '0 0 220px',
      borderRight: `1px solid ${theme.line}`,
      padding: '22px 16px',
      display: 'flex', flexDirection: 'column', gap: 26,
      background: theme.bg,
    }}>
      <div style={{ padding: '0 6px' }}><Wordmark size={64}/></div>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {navItems.map(it => {
          const active = tab === it.id
          return (
            <button key={it.id} onClick={() => setTab(it.id)} style={{
              all: 'unset', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '9px 12px', borderRadius: theme.radius,
              background: active ? theme.surface : 'transparent',
              border: active ? theme.cardBorder : '1px solid transparent',
              color: active ? theme.ink : theme.inkSoft,
              fontFamily: theme.sans, fontSize: 14, fontWeight: active ? 600 : 500,
            }}>
              <it.icon width="18" height="18"/>
              <span>{it.label}</span>
              {active && <span style={{ marginLeft: 'auto', width: 4, height: 18, background: theme.accent, borderRadius: 2 }}/>}
            </button>
          )
        })}
      </nav>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{
          padding: '12px',
          background: theme.surface,
          border: theme.cardBorder, borderRadius: theme.radiusLg,
        }}>
          <div style={{
            fontFamily: theme.sans, fontSize: 9, letterSpacing: 1.4, fontWeight: 700,
            textTransform: 'uppercase', color: theme.muted,
          }}>This week</div>
          <div style={{
            fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
            color: theme.ink, marginTop: 4, lineHeight: 1.1,
          }}>{plannedThisWeek} of 21 <span style={{ color: theme.muted, fontSize: 14 }}>meals planned</span></div>
        </div>
        <UserChip me={me} onLogout={onLogout}/>
      </div>
    </aside>
  )
}

// A small round avatar for `name`/`picture` (falls back to a monogram). Used for
// the badge that the composite avatar overlaps in a corner.
function MiniAvatar({ name, picture, size }: { name: string; picture: string; size: number }) {
  // A surface-colored ring separates the badge from the large avatar it overlaps.
  const cut = `1.5px solid ${theme.bg}`
  return picture
    ? <img src={picture} alt="" width={size} height={size}
        style={{ borderRadius: 999, border: cut, display: 'block' }}/>
    : <div style={{
        width: size, height: size, borderRadius: 999, boxSizing: 'border-box',
        background: theme.ink, color: '#fff', border: cut,
        display: 'grid', placeItems: 'center',
        fontFamily: theme.sans, fontSize: Math.round(size * 0.5), fontWeight: 600,
      }}>{name[0]?.toUpperCase() || '·'}</div>
}

// Avatar shows the real logged-in user's photo as the large background. While
// managing another account it becomes a composite: an accent ring plus the
// managed account's photo as a small badge in a corner — the quiet "you're
// inside someone else's account" cue that replaced the old full-width banner.
// The badge points "outward" toward the screen edge the chip lives against
// (bottom-left in the desktop sidebar, top-right in the mobile header). Pass
// `main` to override whose photo fills the large background (defaults to `me`).
function Avatar({ me, size = 30, main: mainRef, badge, ring, badgeCorner = 'bottom-left' }: {
  me: Me
  size?: number
  main?: { name: string; picture: string }
  badge?: AccountRef
  ring?: boolean
  badgeCorner?: 'bottom-left' | 'top-right'
}) {
  const { name, picture } = mainRef ?? me
  const ringStyle: React.CSSProperties = ring ? { boxShadow: `0 0 0 1.5px ${theme.accent}` } : {}
  const main = picture
    ? <img src={picture} alt="" width={size} height={size}
        style={{ borderRadius: 999, border: `1px solid ${theme.line}`, display: 'block', ...ringStyle }}/>
    : <div style={{
        width: size, height: size, borderRadius: 999,
        background: 'oklch(0.62 0.10 28)',
        display: 'grid', placeItems: 'center',
        fontFamily: theme.serif, fontSize: Math.round(size * 0.47), color: '#fff',
        ...ringStyle,
      }}>{name[0]?.toUpperCase() || '·'}</div>

  if (!badge) return main

  const corner: React.CSSProperties =
    badgeCorner === 'top-right' ? { top: -3, right: -3 } : { bottom: -3, left: -3 }
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {main}
      <div style={{ position: 'absolute', lineHeight: 0, ...corner }}>
        <MiniAvatar name={badge.name} picture={badge.picture} size={Math.round(size * 0.52)}/>
      </div>
    </div>
  )
}

export function UserMenu({ me, onLogout, anchor = 'bottom-left', children }: {
  me: Me
  onLogout: () => void
  anchor?: 'bottom-left' | 'bottom-right' | 'top-left'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      let id2 = 0
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setVisible(true))
      })
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    setVisible(false)
    const t = window.setTimeout(() => setMounted(false), 220)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const go = (to: string) => { setOpen(false); navigate(to) }
  const acting = me.session?.acting_as ?? null

  const opensUpward = anchor === 'top-left'
  const pos: React.CSSProperties =
    anchor === 'bottom-right' ? { top: '100%', right: 0, marginTop: 6 }
    : opensUpward             ? { bottom: '100%', left: 0, marginBottom: 6 }
    :                           { top: '100%', left: 0, marginTop: 6 }

  const originY = opensUpward ? 'bottom' : 'top'
  const slide = opensUpward ? 4 : -4

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={me.name}
        style={{ all: 'unset', cursor: 'pointer', display: 'block' }}
      >
        {children}
      </button>
      {mounted && (
        <div role="menu" style={{
          position: 'absolute', ...pos, zIndex: 100,
          minWidth: 180,
          background: theme.surface,
          border: `1px solid ${theme.lineStrong}`,
          borderRadius: theme.radiusLg,
          boxShadow: '0 6px 20px rgba(34,25,18,0.10)',
          padding: 4,
          display: 'flex', flexDirection: 'column',
          transformOrigin: `${anchor === 'bottom-right' ? 'right' : 'left'} ${originY}`,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : `translateY(${slide}px) scale(0.97)`,
          pointerEvents: visible ? 'auto' : 'none',
          transition: 'opacity 160ms ease, transform 200ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}>
          {acting && (
            <>
              <button
                role="menuitem"
                onClick={() => { setOpen(false); switchTo(null) }}
                style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '8px 10px', borderRadius: theme.radius,
                  display: 'flex', flexDirection: 'column', gap: 1,
                }}
              >
                <span style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted }}>
                  Managing {acting.name}{acting.role !== 'manager' ? ` · ${roleLabel(acting.role)}` : ''}
                </span>
                <span style={{ fontFamily: theme.sans, fontSize: 13, fontWeight: 600, color: theme.accent }}>
                  Return to my account
                </span>
              </button>
              <div style={{ height: 1, background: theme.line, margin: '4px 4px' }}/>
            </>
          )}
          <div style={{
            padding: '8px 10px 6px',
            fontFamily: theme.sans, fontSize: 12, color: theme.muted,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{me.email || me.name}</div>
          <div style={{ height: 1, background: theme.line, margin: '2px 4px 4px' }}/>
          <MenuItem label="Settings" onClick={() => go('/preferences')}/>
          <MenuItem label="Usage" onClick={() => go('/usage')}/>
          <MenuItem label="Sign out" onClick={() => { setOpen(false); onLogout() }}/>
        </div>
      )}
    </div>
  )
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset', cursor: 'pointer',
        padding: '8px 10px', borderRadius: theme.radius,
        fontFamily: theme.sans, fontSize: 13, color: theme.ink,
        background: hover ? theme.surface2 : 'transparent',
      }}
    >
      {label}
    </button>
  )
}

export function UserChip({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const acting = me.session?.acting_as ?? null
  return (
    <UserMenu me={me} onLogout={onLogout} anchor="top-left">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px',
      }}>
        <Avatar me={me} ring={!!acting} main={acting ? me.session.auth_user : undefined} badge={acting ?? undefined} badgeCorner="bottom-left"/>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: theme.sans, fontSize: 13, fontWeight: 500, color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.name}</div>
          <div style={{ fontFamily: theme.sans, fontSize: 11, color: acting ? theme.accent : theme.muted }}>{acting ? 'Managing' : 'Account'}</div>
        </div>
      </div>
    </UserMenu>
  )
}

export function UserChipCompact({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const acting = me.session?.acting_as ?? null
  return (
    <UserMenu me={me} onLogout={onLogout} anchor="bottom-right">
      <Avatar me={me} ring={!!acting} main={acting ? me.session.auth_user : undefined} badge={acting ?? undefined} badgeCorner="top-right"/>
    </UserMenu>
  )
}

export function BottomTabs({ tab, setTab }: { tab: TabId; setTab: (t: TabId) => void }) {
  return (
    <nav style={{
      flex: '0 0 auto',
      borderTop: `1px solid ${theme.line}`,
      background: theme.bg,
      padding: '6px 8px calc(10px + env(safe-area-inset-bottom, 0px))',
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2,
    }}>
      {navItems.map(it => {
        const active = tab === it.id
        return (
          <button key={it.id} onClick={() => setTab(it.id)} style={{
            all: 'unset', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            padding: '8px 4px', borderRadius: theme.radius,
            color: active ? theme.accent : theme.inkSoft,
          }}>
            <it.icon width="20" height="20"/>
            <span style={{
              fontFamily: theme.sans, fontSize: 10, fontWeight: active ? 700 : 500,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>{it.label}</span>
          </button>
        )
      })}
    </nav>
  )
}

export function EmptyTab({ icon: Icon, title, blurb }: { icon: (p: { width: string; height: string }) => ReactNode; title: string; blurb: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>Mila</div>
        <h1 style={{
          fontFamily: theme.serif, fontSize: 42, fontWeight: theme.headWeight,
          color: theme.ink, margin: '2px 0 0', lineHeight: 1.05,
        }}>{title}</h1>
      </div>
      <div style={{
        border: `1px dashed ${theme.lineStrong}`, borderRadius: theme.radiusLg,
        padding: '60px 20px', textAlign: 'center',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        color: theme.muted,
      }}>
        <Icon width="32" height="32"/>
        <div style={{ fontFamily: theme.sans, fontSize: 14 }}>{blurb}</div>
      </div>
    </div>
  )
}
